import { GameVars } from "../../GameVars";
import { GameConstants } from "../../GameConstants";
import { DebugGraphics } from "./DebugGraphics";
import { DiskActor } from "./DiskActor";
import { StrikerSlider } from "./gui/StrikerSlider";
import { AudioManager } from "../../AudioManager";
import * as Carrom from "carrom-king-engine";
import BattleUtils from "../../gzp/BattleUtils";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static readonly CELL_SIZE = 150;

    public static currentInstance: BoardContainer;

    public strikerControlsContainer: Phaser.GameObjects.Container;
    public strikerControlsDotsContainer: Phaser.GameObjects.Container;
    public strikerForcedPx: number;
    public striker: DiskActor;

    private shadowImgsContainer: Phaser.GameObjects.Container;
    private diskImgsContainer: Phaser.GameObjects.Container;
    private debugGraphics: DebugGraphics;
    private diskActors: DiskActor[];
    private tutorialStriker: Phaser.GameObjects.Image;

    public get strikerPosition(): { x: number, y: number } {

        const p = this.striker.position;

        return { x: p.x / GameConstants.PHYS_SCALE, y: p.y / GameConstants.PHYS_SCALE };
    }

    public set strikerPosition(p: { x: number, y: number }) {

        this.striker.position = p;
    }

    constructor(scene: Phaser.Scene) {

        super(scene);

        BoardContainer.currentInstance = this;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = GameConstants.GAME_HEIGHT / 2;
        this.strikerForcedPx = 0;

        let scaleFactor = 1;

        if (GameVars.aspectRatio < 1.4) {
            scaleFactor = .8;
        }

        this.setScale(scaleFactor, scaleFactor * GameVars.scaleY);

        let boardTextureAtlas = GameVars.gameData.boardId < 5 ? "texture_atlas_2" : "texture_atlas_3";

        const board = new Phaser.GameObjects.Image(this.scene, 0, 0, boardTextureAtlas, "board_" + GameVars.gameData.boardId);
        board.setOrigin(.5, .478);
        this.add(board);

        this.strikerControlsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.strikerControlsContainer);

        this.shadowImgsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.shadowImgsContainer);

        this.diskImgsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.diskImgsContainer);

        this.strikerControlsDotsContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.strikerControlsDotsContainer);

        if (GameConstants.SHOW_DEBUG_GRAPHICS) {
            this.addDebugGraphics();
        } else {
            this.debugGraphics = null;
        }

        this.addStrikerAndPucks();
    }

    public update(): void {

        if (GameVars.turnCompleted) {

            if (this.striker.canBePositionedWithCursor) {

                if (GameVars.currentTurnPlayerId === GameVars.playerData.id) {

                    const strikerSlider = StrikerSlider.currentInstance;

                    const x = strikerSlider.cursorPosition;

                    this.striker.position = { x: GameVars.reversedBoard ? -x : x, y: this.striker.position.y / GameConstants.PHYS_SCALE };

                    if (strikerSlider.doesStrikerCollidesWithPucks(x * GameConstants.PHYS_SCALE)) {
                        this.striker.tint();
                    } else {
                        this.striker.unTint();
                    }

                } else {

                    const dx = (this.strikerForcedPx - this.strikerPosition.x) * .1;

                    this.striker.position = { x: this.striker.position.x / GameConstants.PHYS_SCALE + dx, y: this.striker.position.y / GameConstants.PHYS_SCALE };
                }
            }

        } else {

            const disks = GameVars.carromEngine.disks;

            for (let i = 0; i < disks.length; i++) {

                const id = disks[i].id;
                const index = this.getDiskIndex(id);

                if (index !== -1) {

                    const diskActor = this.diskActors[index];
                    diskActor.position = { x: disks[i].position.x, y: disks[i].position.y };
                }
            }
        }
    }

    public addTutorialStriker(): void {

        this.tutorialStriker = new Phaser.GameObjects.Image(this.scene, GameConstants.STRIKER_TUTORIAL_PX * GameConstants.PHYS_SCALE, GameVars.carromEngine.strikerY * GameConstants.PHYS_SCALE, "texture_atlas_1", "striker_gameplay_1");
        this.tutorialStriker.alpha = .35;
        this.diskImgsContainer.add(this.tutorialStriker);
    }

    public removeTutorialStriker(): void {

        this.tutorialStriker.destroy();
    }

    public onShot(): void {

        this.striker.canBePositionedWithCursor = false;
    }

    public removePuck(): void {

        this.striker.hide();
    }

    public onNewTurn(firstTurn?: boolean): void {

        let strikerY = GameVars.carromEngine.strikerY;

        if ((GameVars.reversedBoard && GameVars.currentTurnPlayerId === GameVars.playerData.id) || (!GameVars.reversedBoard && GameVars.currentTurnPlayerId !== GameVars.playerData.id)) {
            strikerY *= -1;
        }

        const puckRadius = GameVars.carromEngine.puckRadius;
        const strikerRadius = GameVars.carromEngine.strikerRadius;
        const strikerSliderWidth = GameVars.carromEngine.strikerSliderWidth;

        const strikerKickPosition = Carrom.Maths.getStrikerKickPosition(strikerY, strikerRadius, strikerSliderWidth, GameVars.pucks, puckRadius);

        if (GameVars.reversedBoard) {
            strikerKickPosition.x *= -1;
        }

        this.strikerForcedPx = strikerKickPosition.x;

        let strikerId: number;
        if (GameVars.currentTurnPlayerId === GameVars.playerData.id) {
            strikerId = GameVars.gameData.strikerId;
        } else {
            if (BattleUtils.doesBattleHasBots) {
                strikerId = GameVars.gameData.strikerId;
            } else {
                strikerId = GameVars.adversaryData.strikerId;
            }
        }

        this.striker.resetStriker(strikerKickPosition, strikerId, firstTurn);
    }

    public onTutorialTurnCompleted(): void {

        this.striker.canBePositionedWithCursor = false;
    }

    public onDiskPocketed(disk: Carrom.Disk): void {

        let index = this.getDiskIndex(disk.id);

        if (index !== -1) {

            const diskActor = this.diskActors[index];
            diskActor.position = { x: disk.position.x, y: disk.position.y };

            if (diskActor.id !== -1) {
                diskActor.pocketed();
                this.diskActors.splice(index, 1);
            }
        }
    }

    public onQueenRestituted(queenPosition: Carrom.Vector2D): void {

        const queen = new DiskActor(this.scene, false, Carrom.Color.RED, 0);

        queen.position = { x: queenPosition.x, y: queenPosition.y };

        this.shadowImgsContainer.add(queen.shadowImg);
        this.diskImgsContainer.add(queen.diskImg);

        if (GameConstants.SHOW_DEBUG_GRAPHICS) {
            this.debugGraphics.add(queen.debugGraphicsContainer);
        }

        this.diskActors.push(queen);
    }

    public onDiskRestituted(disk: any): void {

        const diskActor = new DiskActor(this.scene, false, disk.color, disk.id);

        diskActor.position = { x: disk.position.x, y: disk.position.y };

        this.shadowImgsContainer.add(diskActor.shadowImg);
        this.diskImgsContainer.add(diskActor.diskImg);

        if (GameConstants.SHOW_DEBUG_GRAPHICS) {
            this.debugGraphics.add(diskActor.debugGraphicsContainer);
        }

        this.diskActors.push(diskActor);
    }

    public resetBoard(): void {

        for (let i = 0; i < this.diskActors.length; i++) {
            const disk = this.diskActors[i];
            disk.destroy();
        }

        this.diskActors.length = 0;

        this.addStrikerAndPucks();
    }

    public onContactDiskDisk(disk1Velocity: Carrom.Vector2D, disk2Velocity: Carrom.Vector2D): void {

        const relativeVelocity = disk1Velocity.minus(disk2Velocity).magnitude;
        const volume = relativeVelocity / 3e3;

        AudioManager.playSound("disk-hit", false, volume);
    }

    public onContactDiskBorder(diskNormalVelocity: Carrom.Vector2D): void {

        const volume = diskNormalVelocity.magnitude / 3e3;

        AudioManager.playSound("border-hit", false, volume);
    }

    public onSliderWronglyPlaced(): void {

        this.scene.tweens.add({
            targets: this.tutorialStriker,
            scaleX: 1.25,
            scaleY: 1.25,
            alpha: .8,
            angle: Math.random() > .5 ? 15 : -15,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 100,
            yoyo: true
        });
    }

    public onTutorialStage3(): void {

        this.tutorialStriker.destroy();
        this.striker.position.x = GameConstants.STRIKER_TUTORIAL_PX;
    }

    public setPucksPositions(): void {

        for (let i = 0; i < this.diskActors.length; i++) {
            this.diskActors[i].destroy();
            this.shadowImgsContainer.remove(this.diskActors[i].shadowImg);
            this.diskImgsContainer.remove(this.diskActors[i].diskImg);
        }

        this.addStrikerAndPucks();

        this.striker.diskImg.alpha = 0;
        this.striker.shadowImg.alpha = 0;
    }

    private addStrikerAndPucks(): void {

        this.diskActors = [];

        for (let i = 0; i < GameVars.pucks.length; i++) {

            const puck = new DiskActor(this.scene, false, GameVars.pucks[i].color, GameVars.pucks[i].id);

            puck.position = { x: GameVars.pucks[i].position.x, y: GameVars.pucks[i].position.y };

            this.shadowImgsContainer.add(puck.shadowImg);
            this.diskImgsContainer.add(puck.diskImg);

            if (GameConstants.SHOW_DEBUG_GRAPHICS) {
                this.debugGraphics.add(puck.debugGraphicsContainer);
            }

            this.diskActors.push(puck);
        }

        this.striker = new DiskActor(this.scene, true, null, -1);

        const strikerY = GameVars.carromEngine.strikerY;

        this.striker.position = { x: 0, y: strikerY };

        this.shadowImgsContainer.add(this.striker.shadowImg);
        this.diskImgsContainer.add(this.striker.diskImg);

        if (GameConstants.SHOW_DEBUG_GRAPHICS) {
            this.debugGraphics.add(this.striker.debugGraphicsContainer);
        }

        this.diskActors.push(this.striker);
    }

    private addDebugGraphics(): void {

        this.debugGraphics = new DebugGraphics(this.scene);
        this.add(this.debugGraphics);

        const lineArray = GameVars.carromEngine.lineArray;

        for (let i = 0; i < lineArray.length; i++) {

            const line = lineArray[i];

            this.debugGraphics.drawLine(line.p1.x * GameConstants.PHYS_SCALE, line.p1.y * GameConstants.PHYS_SCALE, line.p2.x * GameConstants.PHYS_SCALE, line.p2.y * GameConstants.PHYS_SCALE, Colors.GREEN);
            this.debugGraphics.drawLine(line.p1Striker.x * GameConstants.PHYS_SCALE, line.p1Striker.y * GameConstants.PHYS_SCALE, line.p2Striker.x * GameConstants.PHYS_SCALE, line.p2Striker.y * GameConstants.PHYS_SCALE, Colors.LILAC, 1);
            this.debugGraphics.drawLine(line.p1Puck.x * GameConstants.PHYS_SCALE, line.p1Puck.y * GameConstants.PHYS_SCALE, line.p2Puck.x * GameConstants.PHYS_SCALE, line.p2Puck.y * GameConstants.PHYS_SCALE, Colors.MAROON, 1);
        }

        const pocketArray = GameVars.carromEngine.pocketArray;
        const pocketRadius = GameVars.carromEngine.pocketRadius;

        for (let i = 0; i < pocketArray.length; i++) {

            const pocket = pocketArray[i];

            this.debugGraphics.drawCircle(pocket.position.x * GameConstants.PHYS_SCALE, pocket.position.y * GameConstants.PHYS_SCALE, pocketRadius * GameConstants.PHYS_SCALE, Colors.YELLOW);
        }
    }

    private getDiskIndex(id: number): number {

        let index = -1;

        for (let i = 0; i < this.diskActors.length; i++) {
            if (this.diskActors[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    }
}
